export const COLOR_SCHEMES = ["light", "dark"] as const;

export type ColorScheme = (typeof COLOR_SCHEMES)[number];

export const COLOR_THEMES = [
	"brand",
	"mist",
	"summer",
	"winter",
	"earth",
	"sun",
] as const;

export type ColorTheme = (typeof COLOR_THEMES)[number];

export type PrimaryName =
	| "playRed"
	| "playMist"
	| "playGreen"
	| "playBlue"
	| "playBrown"
	| "playSun";

export const playRed = [
	"#FDECEE",
	"#F8C9CD",
	"#F19AA1",
	"#E96B74",
	"#E3414C",
	"#E31C23",
	"#C0161C",
	"#9A1116",
	"#700C10",
	"#47080A",
] as const;

export const playMist = [
	"#F1F5F8",
	"#DCE6ED",
	"#B9CAD7",
	"#8FAABD",
	"#6A8BA3",
	"#4E718A",
	"#3E5A6E",
	"#2E4353",
	"#1F2D38",
	"#121A21",
] as const;

export const playBlue = [
	"#EBF1FE",
	"#D6E3FC",
	"#ADC7F9",
	"#84ABF5",
	"#5B8FF2",
	"#2F6FED",
	"#2459BE",
	"#1A438E",
	"#112C5F",
	"#08162F",
] as const;

export const playGreen = [
	"#E8F7F0",
	"#D1EFE1",
	"#A3DFC3",
	"#75CFA5",
	"#47BF87",
	"#2F9E6B",
	"#267E56",
	"#1C5F40",
	"#133F2B",
	"#092015",
] as const;

export const playBrown = [
	"#F6F1EB",
	"#E8D9C8",
	"#D4B896",
	"#C09A6E",
	"#A67C52",
	"#8B5E34",
	"#704A28",
	"#56371D",
	"#3C2514",
	"#24160C",
] as const;

export const playSun = [
	"#FFF6EB",
	"#FFE8CC",
	"#FFD199",
	"#FFB866",
	"#F59E3B",
	"#E07A1F",
	"#C46212",
	"#9A4A0C",
	"#6B3208",
	"#3D1C04",
] as const;

export const THEME_TOKENS: Record<
	ColorTheme,
	{ primary: PrimaryName; canvasBg: Record<ColorScheme, string> }
> = {
	brand: {
		primary: "playRed",
		canvasBg: { light: "#FBF4F4", dark: "#1A1012" },
	},
	mist: {
		primary: "playMist",
		canvasBg: { light: "#F3F6F8", dark: "#12171C" },
	},
	summer: {
		primary: "playGreen",
		canvasBg: { light: "#F3F8F5", dark: "#101614" },
	},
	winter: {
		primary: "playBlue",
		canvasBg: { light: "#F5F7FA", dark: "#0F141C" },
	},
	earth: {
		primary: "playBrown",
		canvasBg: { light: "#F7F3EE", dark: "#16110D" },
	},
	sun: { primary: "playSun", canvasBg: { light: "#FBF4EA", dark: "#1A140E" } },
};

export const PLAY_COLOR_TUPLES = {
	playRed,
	playMist,
	playBlue,
	playGreen,
	playBrown,
	playSun,
} as const;

function isColorTheme(raw: string | null | undefined): raw is ColorTheme {
	return (COLOR_THEMES as readonly string[]).includes(raw ?? "");
}

function isColorScheme(raw: string | null | undefined): raw is ColorScheme {
	return (COLOR_SCHEMES as readonly string[]).includes(raw ?? "");
}

export function parseColorTheme(raw: string | null | undefined): ColorTheme {
	return isColorTheme(raw) ? raw : "brand";
}

export function parseColorScheme(raw: string | null | undefined): ColorScheme {
	return isColorScheme(raw) ? raw : "light";
}

export function canvasBgOf(theme: ColorTheme, scheme: ColorScheme): string {
	return THEME_TOKENS[theme].canvasBg[scheme];
}

export function themeSemantics(scheme: ColorScheme) {
	return {
		success: "#2F9E6B",
		danger: "#E5484D",
		warning: "#D97706",
		muted: scheme === "dark" ? "#9CA3AF" : "#6B7280",
	};
}
